import styled from 'styled-components';
import { textColor, THEME_PRIMARY, THEME_RED, THEME_WARNING } from '../../../styles/basics/colors';
import {
    LC_PRODUCT_STATE_LIVE_IN_COLLECTION,
    LC_PRODUCT_STATE_LIVE_INACTIVE,
    LC_PRODUCT_STATE_NEW,
    LC_PRODUCT_STATE_PROCESSING,
    LC_PRODUCT_STATE_UPDATED
} from '../product/LCStatusIndicator/StatusIndicatorStepState';
import { useTranslation } from 'react-i18next';

const StyledProductStatus = styled.span<{ color: string | null }>`
    font-weight: bold;
    ${props => (props.color ? textColor(props.color) : null)} !important;
`;

StyledProductStatus.displayName = 'ProductStatusLine__StyledProductStatus';

interface LiveCollectionProps {
    newState?: boolean;
    updated?: boolean;
    processing?: boolean;
}
const LiveCollectionProductStatus = ({
    newState = false,
    updated = false,
    processing = false
}: LiveCollectionProps) => {
    const { t } = useTranslation();
    return (
        <p>
            <StyledProductStatus color={THEME_PRIMARY}>{t('yourLocalContent.yourTitles.live')} </StyledProductStatus>
            {newState || updated ? (
                <StyledProductStatus color={THEME_WARNING}>
                    {t('yourLocalContent.yourTitles.newUpdated')}
                </StyledProductStatus>
            ) : null}
            {processing ? ' - ' : null}
            {processing ? (
                <StyledProductStatus color={THEME_WARNING}>
                    {t('yourLocalContent.yourTitles.processing')}
                </StyledProductStatus>
            ) : null}
        </p>
    );
};

interface ProductStatusProps {
    inactive?: boolean;
    processing?: boolean;
    newState?: boolean;
    updated?: boolean;
}

function ProductStatus({
    inactive = false,
    processing = false,
    newState = false,
    updated = false
}: ProductStatusProps) {
    const { t } = useTranslation();
    let text: string;
    let color: string = '';

    if (inactive) {
        text = t('yourLocalContent.yourTitles.inactive');
        color = THEME_RED;
    } else if (newState || updated) {
        text = t('yourLocalContent.yourTitles.newUpdated');
        color = THEME_WARNING;
    } else if (processing) {
        text = t('yourLocalContent.yourTitles.processing');
        color = THEME_WARNING;
    } else {
        text = '';
    }
    return (
        <p>
            <StyledProductStatus data-testid="status-line" color={color}>
                {text}
            </StyledProductStatus>
        </p>
    );
}

export type Status =
    | typeof LC_PRODUCT_STATE_LIVE_IN_COLLECTION
    | typeof LC_PRODUCT_STATE_LIVE_INACTIVE
    | typeof LC_PRODUCT_STATE_NEW
    | typeof LC_PRODUCT_STATE_PROCESSING
    | typeof LC_PRODUCT_STATE_UPDATED;

const ProductStatusLine = ({ status }: { status: Status[] }) => {
    if (!status || !Array.isArray(status)) return null;
    const processing = status.includes(LC_PRODUCT_STATE_PROCESSING) ?? false;
    const newState = status.includes(LC_PRODUCT_STATE_NEW) ?? false;
    const updated = status.includes(LC_PRODUCT_STATE_UPDATED) ?? false;
    const liveCollection = status.includes(LC_PRODUCT_STATE_LIVE_IN_COLLECTION) ?? false;
    const inactive = status.includes(LC_PRODUCT_STATE_LIVE_INACTIVE) ?? false;

    if (liveCollection) {
        return <LiveCollectionProductStatus newState={newState} updated={updated} processing={processing} />;
    }
    return <ProductStatus inactive={inactive} processing={processing} newState={newState} updated={updated} />;
};

export default ProductStatusLine;
